import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatMinutes, JOB_COST_KINDS, type JobCosting } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { idem, tap } from '../../lib/idempotency';
import { useSession } from '../session';
import { SectionCard } from '../ui/Detail';
import { StatusBadge, Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import { useTapOnce } from '../ui/useTapOnce';
import { sendOnce } from '../tickets/sendOnce';
import { ErrorState, awaitingServer, mutationState } from '../ui/QueryStates';
import { Skeleton } from '../ui/Skeleton';
import { money } from '../money';
import { IconMoney, IconPlay, IconStop, IconTrash, IconPlus } from '../icons';
import { revenueFigure } from './basis';
import { PutBackButton } from '../inventory/PutBackButton';
import { PartsBillingCard } from './PartsBillingCard';
import { BillingLogCard } from './BillingLogCard';

/**
 * "Did we make money on this job?" — the question Jobber can't answer at the invoice
 * or on recurring work (PRD v2 §7). Labor comes from timers, parts and subs from job
 * costs, revenue from the issued invoices before tax (or the approved quote until one
 * exists, named as the quote it is: `basis.ts`).
 *
 * The server computes the margin; this panel only renders it — including the parts it
 * would be flattering to omit. Unrated labor gets its own warning line rather than
 * quietly inflating the margin, and the basis is always named out loud.
 */

const BASIS_LABEL: Record<JobCosting['basis'], string> = {
  invoiced: 'vs. invoiced',
  quoted: 'vs. approved quote',
  none: 'nothing billed yet',
};

const KIND_LABEL: Record<string, string> = {
  material: 'Parts',
  subcontractor: 'Sub',
  equipment: 'Equipment',
  permit: 'Permit',
  other: 'Other',
};

/**
 * The bar is always the whole of the larger of revenue and cost, so the slices can never
 * sum past 100%. Only a POSITIVE margin earns a slice — the width left over after cost.
 * When cost has overrun revenue there is no room left to draw, which is the point: the
 * bar is full of cost, and the overrun is stated as a number instead.
 */
function MarginBar({ c }: { c: JobCosting }) {
  const denominator = Math.max(c.revenueCents, c.totalCostCents, 1);
  const pct = (n: number) => `${Math.max(0, (n / denominator) * 100)}%`;
  const headroomCents = Math.max(0, c.revenueCents - c.totalCostCents);

  // With no revenue there is no margin to speak of — only cost so far. Calling that
  // "Over" would invent a budget the job doesn't have yet.
  const outcome =
    c.basis === 'none'
      ? { label: 'Cost so far', value: c.totalCostCents, swatch: 'var(--sw-muted)' }
      : c.marginCents < 0
        ? { label: 'Over', value: c.marginCents, swatch: 'var(--sw-danger-text)' }
        : { label: 'Margin', value: c.marginCents, swatch: 'var(--sw-success-text)' };

  return (
    <div>
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-surface-2">
        <div
          className="bg-[var(--sw-warn-text)]"
          style={{ width: pct(c.laborCents) }}
          title={`Labor ${money(c.laborCents)}`}
        />
        <div
          className="bg-[var(--sw-info-text)]"
          style={{ width: pct(c.materialCents) }}
          title={`Parts + other ${money(c.materialCents)}`}
        />
        {headroomCents > 0 ? (
          <div
            className="bg-[var(--sw-success-text)]"
            style={{ width: pct(headroomCents) }}
            title={`Margin ${money(headroomCents)}`}
          />
        ) : null}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <Legend swatch="var(--sw-warn-text)" label="Labor" value={money(c.laborCents)} />
        <Legend
          swatch="var(--sw-info-text)"
          label="Parts"
          value={money(c.materialCents)}
        />
        <Legend
          swatch={outcome.swatch}
          label={outcome.label}
          value={money(outcome.value)}
        />
      </div>
    </div>
  );
}

function Legend({
  swatch,
  label,
  value,
}: {
  swatch: string;
  label: string;
  value: string;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-block h-2 w-2 rounded-full"
        style={{ backgroundColor: swatch }}
        aria-hidden
      />
      {label}
      <span className="font-medium tabular-nums text-text">{value}</span>
    </span>
  );
}

export function JobMoneyPanel({ jobId }: { jobId: string }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const { role } = useSession();
  const [adding, setAdding] = useState(false);
  const [loggingTime, setLoggingTime] = useState(false);
  // The time entry whose Delete was pressed once: a second press says yes (ticket 13: a delete
  // takes the entry's line off a draft, or leaves billed hours owed back).
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const costing = useQuery(trpc.costing.forJob.queryOptions({ jobId }));
  const running = useQuery(trpc.costing.myRunningTimer.queryOptions());
  const refresh = () => void qc.invalidateQueries();

  const startTimer = useMutation(
    trpc.costing.startTimer.mutationOptions({ onSuccess: refresh }),
  );
  const stopTimer = useMutation(
    trpc.costing.stopTimer.mutationOptions({ onSuccess: refresh }),
  );
  const addCost = useMutation(
    trpc.costing.addCost.mutationOptions({
      onSuccess: () => {
        setAdding(false);
        refresh();
      },
    }),
  );
  // Settled, not only succeeded: a refusal ("Job cost not found", another tab got there first)
  // means this list is stale, so it reloads either way.
  const deleteCost = useMutation(
    trpc.costing.deleteCost.mutationOptions({ onSettled: refresh }),
  );
  // A part's cost leaves the job the way the part does: back where it came from. Deleting it
  // alone would leave the van short for good (build-map 045), so the server refuses that.
  const returnPart = useMutation(
    trpc.inventory.returnFromJob.mutationOptions({ onSettled: refresh }),
  );
  // One list, one write at a time, and one notice: the error of the action pressed LAST. Each
  // press clears the other's old error, so a refusal never outlives the next thing the office
  // does, nor hides it. `awaitingServer` counts a press queued offline too (QueryStates).
  const costBusy = awaitingServer(returnPart) || awaitingServer(deleteCost);
  const costError = returnPart.error ?? deleteCost.error;
  const putBack = (jobCostId: string) => {
    if (costBusy) return;
    deleteCost.reset();
    returnPart.mutate({ jobCostId, ...idem() });
  };
  const removeCost = (jobCostId: string) => {
    if (costBusy) return;
    returnPart.reset();
    deleteCost.mutate({ jobCostId, ...idem() });
  };
  const logTime = useMutation(
    trpc.costing.logTime.mutationOptions({
      onSuccess: () => {
        setLoggingTime(false);
        refresh();
      },
    }),
  );
  // One delete per tap: a double press on Delete sends it once.
  const deleteLock = useTapOnce();
  const deleteTime = useMutation(
    trpc.costing.deleteTimeEntry.mutationOptions({
      onSettled: () => {
        deleteLock.release();
        setConfirmDelete(null);
        refresh();
      },
    }),
  );
  // What the clock and the time list were refused, most recent first: a closed T&M job takes no
  // more time (ticket 13), and the office should see why nothing happened.
  const timeError =
    deleteTime.error ?? logTime.error ?? startTimer.error ?? stopTimer.error;

  const runningHere = running.data?.entry.jobId === jobId ? running.data.entry : null;
  const runningElsewhere = running.data && running.data.entry.jobId !== jobId;

  const timerButton = (
    <div className="flex items-center gap-2">
      {runningHere ? (
        <Button
          variant="danger"
          onClick={() => stopTimer.mutate({ timeEntryId: runningHere.id, ...tap() })}
          disabled={stopTimer.isPending}
        >
          <IconStop size={15} />
          Stop timer
        </Button>
      ) : (
        <Button
          variant="ghost"
          onClick={() => startTimer.mutate({ jobId, ...tap() })}
          disabled={startTimer.isPending || Boolean(runningElsewhere)}
          title={
            runningElsewhere
              ? `Your timer is running on ${running.data?.job.title}`
              : undefined
          }
        >
          <IconPlay size={15} />
          Start timer
        </Button>
      )}
    </div>
  );

  if (costing.isPending) {
    return (
      <SectionCard title="Money on this job">
        <Skeleton className="h-24 w-full" />
      </SectionCard>
    );
  }
  if (costing.isError) {
    return (
      <SectionCard title="Money on this job">
        <ErrorState
          icon={IconMoney}
          what="the job's costing"
          onRetry={() => void costing.refetch()}
        />
      </SectionCard>
    );
  }

  const { summary: c, timeEntries, costs } = costing.data;
  const measuredAgainst = revenueFigure('job', c.basis);

  return (
    <div className="space-y-4">
      <SectionCard title="Money on this job" right={timerButton}>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <p className="font-heading text-2xl font-bold tabular-nums">
              {c.basis === 'none' ? '—' : money(c.marginCents)}
            </p>
            <p className="text-xs text-muted-foreground">
              Margin {c.marginBps === null ? '' : `· ${(c.marginBps / 100).toFixed(1)}%`}{' '}
              <StatusBadge tone={c.basis === 'none' ? 'neutral' : 'info'}>
                {BASIS_LABEL[c.basis]}
              </StatusBadge>
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm font-semibold tabular-nums">
              {c.basis === 'none' ? '—' : money(c.revenueCents)}
            </p>
            <p className="text-xs text-muted-foreground">{measuredAgainst.label}</p>
            <p className="text-xs text-muted-foreground">{measuredAgainst.line}</p>
          </div>
        </div>

        <div className="mt-3">
          <MarginBar c={c} />
        </div>

        {c.unratedMinutes > 0 ? (
          // The margin is knowably incomplete. Say so where the number is, not in a footnote.
          <div className="mt-3">
            <Notice tone="warn">
              {formatMinutes(c.unratedMinutes)} logged by someone with no cost rate — this
              margin is optimistic.{' '}
              {role === 'owner' ? (
                <Link to="/settings" className="font-semibold underline">
                  Set hourly costs
                </Link>
              ) : (
                'Ask an owner to set hourly costs.'
              )}
            </Notice>
          </div>
        ) : null}

        {c.basis === 'none' ? (
          <p className="mt-3 text-xs text-muted-foreground">
            Nothing quoted or invoiced on this job yet, so there's no revenue to measure
            against. Costs are still being tracked.
          </p>
        ) : null}
      </SectionCard>

      <SectionCard
        title={`Labor (${formatMinutes(c.laborMinutes)})`}
        right={
          <button
            type="button"
            onClick={() => setLoggingTime((v) => !v)}
            className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--sw-brand-text)] hover:underline"
          >
            <IconPlus size={13} />
            Log time
          </button>
        }
      >
        {timeError ? <Notice tone="danger">{timeError.message}</Notice> : null}
        {loggingTime ? (
          <LogTimeForm
            jobId={jobId}
            pending={logTime.isPending}
            onCancel={() => setLoggingTime(false)}
            onSubmit={(v) => logTime.mutate({ ...v, ...idem() })}
          />
        ) : null}

        {timeEntries.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No time logged yet. Start the timer, or log hours after the fact.
          </p>
        ) : (
          <ul className="divide-y divide-border text-sm">
            {timeEntries.map((e) => {
              const minutes = e.endedAt
                ? Math.round(
                    (new Date(e.endedAt).getTime() - new Date(e.startedAt).getTime()) /
                      60000,
                  )
                : 0;
              return (
                <li key={e.id} className="flex flex-wrap items-center gap-3 py-2">
                  <span className="w-28 shrink-0 text-xs text-muted-foreground">
                    {new Date(e.startedAt).toLocaleDateString()}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{e.userName}</span>
                  {e.endedAt === null ? (
                    <StatusBadge tone="brand">Running</StatusBadge>
                  ) : (
                    <span className="tabular-nums">{formatMinutes(minutes)}</span>
                  )}
                  <span className="w-20 shrink-0 text-right tabular-nums text-muted-foreground">
                    {e.costRateCents === null ? (
                      <span title="No cost rate on file">—</span>
                    ) : (
                      money(Math.round((minutes / 60) * e.costRateCents))
                    )}
                  </span>
                  {confirmDelete === e.id ? (
                    <span className="flex basis-full flex-wrap items-center justify-end gap-2 text-xs">
                      <span className="text-muted-foreground">
                        Delete this time? On an invoice's draft, its line comes off; on an
                        issued invoice, the customer is owed it back.
                      </span>
                      <Button
                        variant="ghost"
                        disabled={deleteTime.isPending}
                        onClick={() => setConfirmDelete(null)}
                      >
                        Keep it
                      </Button>
                      <Button
                        variant="danger"
                        disabled={deleteTime.isPending}
                        onClick={() =>
                          sendOnce(deleteLock, deleteTime.mutate, { timeEntryId: e.id })
                        }
                      >
                        {deleteTime.isPending ? 'Deleting…' : 'Delete'}
                      </Button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      aria-label={`Delete time entry for ${e.userName}`}
                      disabled={deleteTime.isPending}
                      onClick={() => setConfirmDelete(e.id)}
                      className="rounded-[var(--radius-sm)] p-1 text-muted-foreground hover:bg-surface-2 hover:text-[var(--sw-danger-text)] disabled:opacity-40"
                    >
                      <IconTrash size={14} />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </SectionCard>

      {/* Fixed jobs: which parts the quote covers, and Add to invoice for the rest (Inventory v2). */}
      <PartsBillingCard jobId={jobId} />
      {/* Every price change, discount, write-off and charge, with who and when (12d). */}
      <BillingLogCard jobId={jobId} />

      <SectionCard
        title={`Parts + other costs (${money(c.materialCents)})`}
        right={
          <button
            type="button"
            onClick={() => setAdding((v) => !v)}
            className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--sw-brand-text)] hover:underline"
          >
            <IconPlus size={13} />
            Add cost
          </button>
        }
      >
        {adding ? (
          <AddCostForm
            jobId={jobId}
            pending={addCost.isPending}
            onCancel={() => setAdding(false)}
            onSubmit={(v) => addCost.mutate({ ...v, ...idem() })}
          />
        ) : null}

        {costError ? <Notice tone="danger">{costError.message}</Notice> : null}

        {costs.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No parts or other costs logged. When a tech marks a part used on their phone,
            it comes off the van and lands here by itself.
          </p>
        ) : (
          // A size container: on a phone-width list the kind badge gives its room to the
          // name, measured by the LIST's width, not the window's (LESSONS: `@container`).
          <ul className="@container divide-y divide-border text-sm">
            {costs.map((cost) => (
              <li key={cost.id} className="flex items-center gap-3 py-2">
                <span className="hidden shrink-0 @sm:inline">
                  <StatusBadge tone="neutral">
                    {KIND_LABEL[cost.kind] ?? cost.kind}
                  </StatusBadge>
                </span>
                {/* The quantity sits UNDER the name, as on the parts card: beside it, it and
                    the Put back button left a phone's row no room for the name at all. */}
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{cost.description}</span>
                  <span className="block text-xs text-muted-foreground tabular-nums">
                    {Number(cost.qty)} × {money(cost.unitCostCents)}
                  </span>
                </span>
                <span className="w-20 shrink-0 text-right font-medium tabular-nums">
                  {money(cost.totalCostCents)}
                </span>
                {/* `aria-disabled`, never `disabled`, on both: a control that turns disabled
                    while focused drops focus to <body> (LESSONS 2026-09-28). */}
                {cost.stockMovementId ? (
                  <PutBackButton
                    cost={cost}
                    listBusy={costBusy}
                    label={putBackLabel(returnPart, cost.id)}
                    onPress={() => putBack(cost.id)}
                  />
                ) : (
                  <button
                    type="button"
                    aria-label={`Delete cost ${cost.description}`}
                    aria-disabled={costBusy}
                    onClick={() => removeCost(cost.id)}
                    className="rounded-[var(--radius-sm)] p-1 text-muted-foreground hover:bg-surface-2 hover:text-[var(--sw-danger-text)] aria-disabled:pointer-events-none aria-disabled:opacity-40"
                  >
                    <IconTrash size={14} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}

/**
 * What the Put back button on `jobCostId`'s row says while a return is in flight. Only the
 * row that was pressed changes its words; `isPaused` before `isPending` (`mutationState`), or
 * a press made offline would read "Putting back…" for as long as the signal is gone.
 */
function putBackLabel(
  returnPart: Parameters<typeof mutationState>[0] & {
    variables?: { jobCostId: string };
  },
  jobCostId: string,
): string {
  if (returnPart.variables?.jobCostId !== jobCostId) return 'Put back';
  const phase = mutationState(returnPart);
  if (phase === 'queued') return 'Waiting to send';
  if (phase === 'sending') return 'Putting back…';
  return 'Put back';
}

const fieldClass =
  'w-full rounded-[var(--radius-sm)] border border-border bg-surface px-2.5 py-1.5 text-sm';

function AddCostForm({
  jobId,
  pending,
  onCancel,
  onSubmit,
}: {
  jobId: string;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (v: {
    jobId: string;
    kind: (typeof JOB_COST_KINDS)[number];
    description: string;
    qty: number;
    unitCostCents: number;
    vendorName?: string;
  }) => void;
}) {
  const [kind, setKind] = useState<(typeof JOB_COST_KINDS)[number]>('material');
  const [description, setDescription] = useState('');
  const [qty, setQty] = useState('1');
  const [unitCost, setUnitCost] = useState('');
  const [vendor, setVendor] = useState('');

  const cents = Math.round(Number(unitCost || 0) * 100);
  const valid = description.trim().length > 0 && cents > 0 && Number(qty) > 0;

  return (
    <form
      className="mb-3 space-y-2 rounded-[var(--radius-sm)] border border-border bg-surface-2 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid) return;
        onSubmit({
          jobId,
          kind,
          description: description.trim(),
          qty: Number(qty),
          unitCostCents: cents,
          vendorName: vendor.trim() || undefined,
        });
      }}
    >
      <div className="grid gap-2 sm:grid-cols-[130px_1fr]">
        <label className="text-xs font-medium">
          <span className="mb-1 block text-muted-foreground">Type</span>
          <select
            className={fieldClass}
            value={kind}
            onChange={(e) => setKind(e.target.value as (typeof JOB_COST_KINDS)[number])}
          >
            {JOB_COST_KINDS.map((k) => (
              <option key={k} value={k}>
                {KIND_LABEL[k] ?? k}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs font-medium">
          <span className="mb-1 block text-muted-foreground">What was it</span>
          <input
            className={fieldClass}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="5-ton compressor"
            autoFocus
          />
        </label>
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        <label className="text-xs font-medium">
          <span className="mb-1 block text-muted-foreground">Qty</span>
          <input
            className={fieldClass}
            type="number"
            min="0.01"
            step="0.01"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
          />
        </label>
        <label className="text-xs font-medium">
          <span className="mb-1 block text-muted-foreground">Unit cost ($)</span>
          <input
            className={fieldClass}
            type="number"
            min="0"
            step="0.01"
            value={unitCost}
            onChange={(e) => setUnitCost(e.target.value)}
            placeholder="0.00"
          />
        </label>
        <label className="text-xs font-medium">
          <span className="mb-1 block text-muted-foreground">Vendor</span>
          <input
            className={fieldClass}
            value={vendor}
            onChange={(e) => setVendor(e.target.value)}
            placeholder="Optional"
          />
        </label>
      </div>
      <p className="text-xs text-muted-foreground">
        This is what it <strong>cost you</strong>, not what you charge — that's the line
        item on the invoice.
      </p>
      <div className="flex gap-2">
        <Button type="submit" disabled={!valid || pending}>
          {pending ? 'Adding…' : 'Add cost'}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/** Local datetime-local value for "now minus n hours", in the browser's own zone. */
function localInput(offsetHours: number): string {
  const d = new Date(Date.now() - offsetHours * 3600_000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function LogTimeForm({
  jobId,
  pending,
  onCancel,
  onSubmit,
}: {
  jobId: string;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (v: {
    jobId: string;
    userId?: string;
    startedAt: Date;
    endedAt: Date;
  }) => void;
}) {
  const trpc = useTRPC();
  const members = useQuery(trpc.org.members.queryOptions());
  const [userId, setUserId] = useState('');
  const [start, setStart] = useState(() => localInput(2));
  const [end, setEnd] = useState(() => localInput(0));

  const valid = start && end && new Date(end) > new Date(start);

  return (
    <form
      className="mb-3 space-y-2 rounded-[var(--radius-sm)] border border-border bg-surface-2 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid) return;
        onSubmit({
          jobId,
          userId: userId || undefined,
          startedAt: new Date(start),
          endedAt: new Date(end),
        });
      }}
    >
      <div className="grid gap-2 sm:grid-cols-3">
        <label className="text-xs font-medium">
          <span className="mb-1 block text-muted-foreground">Who</span>
          <select
            className={fieldClass}
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
          >
            <option value="">Me</option>
            {(members.data ?? []).map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.name ?? m.email}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs font-medium">
          <span className="mb-1 block text-muted-foreground">Started</span>
          <input
            className={fieldClass}
            type="datetime-local"
            value={start}
            onChange={(e) => setStart(e.target.value)}
          />
        </label>
        <label className="text-xs font-medium">
          <span className="mb-1 block text-muted-foreground">Finished</span>
          <input
            className={fieldClass}
            type="datetime-local"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
          />
        </label>
      </div>
      {!valid ? (
        <p className="text-xs text-[var(--sw-danger-text)]">
          The finish time has to be after the start time.
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={!valid || pending}>
          {pending ? 'Logging…' : 'Log time'}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
